"use client"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { extractColorSwatchesFromProducts } from "@/app/(site)/_utils/transformData/extractColorSwatchesFromProducts"
import {
    selectColorSwatch,
    setAvailableSizes,
    setColorSwatches,
    updateCanAddToCart,
    useAppDispatch,
    useAppSelector
} from "@/app/(site)/contexts/Global"
import { ColorSwatch as ColorSwatchType } from "@/app/(site)/contexts/Global/types"
import {
    RelatedProduct as ContentServiceRelatedProduct,
    ProductData,
    SizeInfo
} from "@/lib/content-service/types/ContentServiceProduct"
import { pdpChangeSkuEvent } from "@/lib/utils/tracking/gtmEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import Image from "next/image"
import { useParams } from "next/navigation"
import React from "react"

interface ColorSwatchProps {
    initialProductValue: (
        | CheckoutAPIProduct
        | RelatedProduct
        | ProductData
        | ContentServiceRelatedProduct
    )[]
    sizes: SizeInfo[]
    currentProduct?: CheckoutAPIProduct | RelatedProduct | ProductData
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({
    initialProductValue,
    sizes,
    currentProduct
}) => {
    const currentUri = useParams().slug
    const dispatch = useAppDispatch()
    const availableSizesContext = useAppSelector(state => state.product.availableSizes)
    const siteContext = useSiteContext()
    const colorSwatches = extractColorSwatchesFromProducts(initialProductValue)

    React.useEffect(() => {
        dispatch(setColorSwatches(colorSwatches))
        dispatch(selectColorSwatch(colorSwatches?.[0]?.color_name))
    }, [colorSwatches])

    React.useEffect(() => {
        // Update the sizes to the state context
        const updatedSizes = sizes.map(size => {
            const existingSize = availableSizesContext.find(s => s.sizeId === size.sizeId)
            return {
                ...size,
                isSelected: existingSize ? existingSize.isSelected : false
            }
        })
        dispatch(setAvailableSizes(updatedSizes))
    }, [])

    const handleChangeVariant = (
        selectedColorSwatch: ColorSwatchType,
        initialSizeValue: SizeInfo[]
    ) => {
        return async () => {
            // Track color change
            if (currentProduct) {
                pdpChangeSkuEvent(
                    "change_colour",
                    currentProduct.sku || (currentProduct as any).id,
                    currentProduct.name,
                    selectedColorSwatch.color_name,
                    siteContext
                )
            }

            // Find the selected size from initialSizeValue
            const selectedSize = initialSizeValue.find(s => s.isAvailable)?.sizeId

            // Update the isAvailable property of availableSizesContext based on initialSizeValue
            const updatedSizes = availableSizesContext.map(s => {
                const isAvailable =
                    initialSizeValue.find(initialSize => initialSize.sizeId === s.sizeId)
                        ?.isAvailable || s.isAvailable

                return {
                    ...s,
                    isSelected: s.sizeId === selectedSize,
                    isAvailable: isAvailable
                }
            })

            // Call the setAvailableSizes function to update the sizes
            dispatch(setAvailableSizes(updatedSizes))
            dispatch(selectColorSwatch(selectedColorSwatch.color_name))
            dispatch(updateCanAddToCart())
        }
    }

    const sortedSwatches = React.useMemo(() => {
        return [...colorSwatches].sort((a, b) => {
            const orderA = a.sortOrder || Infinity
            const orderB = b.sortOrder || Infinity
            return orderA - orderB || (a?.uri || "").localeCompare(b?.uri || "")
        })
    }, [colorSwatches])

    return (
        <div className="flex flex-wrap items-center justify-start gap-3 md:gap-8">
            {sortedSwatches.map((swatch, index) => {
                const isSwatchImageAvailable = swatch?.image?.url.length > 0
                const lens = swatch?.lens
                const swatchLensHex = lens?.hex
                const isSwatchAvailable = swatch?.isAvailable
                const swatchHex = swatch?.hex

                return (
                    <DynamicLink
                        key={`swatch.uri-${index}`}
                        href={`/product/${swatch.uri}?source=color_swatch`}
                        passHref
                        scroll={false}
                        shallow
                        prefetch
                        replace
                    >
                        <div
                            className={`relative ${
                                swatch.uri === currentUri
                                    ? "rounded-full border border-black p-2"
                                    : "p-[3px]"
                            }`}
                        >
                            <span
                                onClick={() => handleChangeVariant(swatch, sizes)()}
                                className="block cursor-pointer rounded-full border border-main-medium-grey"
                            >
                                {lens ? (
                                    <div className="absolute h-40 w-40 rotate-[-50deg]">
                                        <div className="relative h-40 w-40">
                                            {isSwatchImageAvailable ? (
                                                <Image
                                                    alt={swatch?.color_name}
                                                    src={swatch?.image?.url}
                                                    width={100}
                                                    height={100}
                                                    className="absolute inset-0 rounded-full"
                                                    style={{
                                                        clipPath:
                                                            "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                        backgroundColor: swatchLensHex
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="absolute inset-0 rounded-full"
                                                    style={{
                                                        clipPath:
                                                            "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                        backgroundColor:
                                                            swatchLensHex ?? swatchHex
                                                    }}
                                                ></div>
                                            )}
                                            <div
                                                className="absolute inset-0 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                    backgroundColor: swatchHex
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {isSwatchAvailable ? (
                                    <div
                                        style={{ backgroundColor: swatchHex }}
                                        className="h-40 w-40 rounded-full p-1"
                                    />
                                ) : (
                                    <div className="relative h-40 w-40 rotate-[-50deg] rounded-full">
                                        {isSwatchImageAvailable ? (
                                            <Image
                                                alt={swatch?.color_name}
                                                src={swatch?.image?.url}
                                                width={100}
                                                height={100}
                                                className="absolute inset-0 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                    backgroundColor: swatchLensHex
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="absolute inset-0 rounded-full"
                                                style={{
                                                    clipPath:
                                                        "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                    backgroundColor:
                                                        swatchLensHex ?? swatchHex
                                                }}
                                            ></div>
                                        )}
                                        <div
                                            className="absolute inset-0 rounded-full"
                                            style={{
                                                clipPath:
                                                    "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                backgroundColor: swatchHex
                                            }}
                                        ></div>
                                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-white bg-opacity-40">
                                            {swatchHex === "#ffffff" ? (
                                                <div className="absolute h-42 w-4 rotate-[95deg] bg-black" />
                                            ) : (
                                                <div className="absolute h-42 w-2 rotate-[95deg] bg-main-medium-grey" />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </span>
                        </div>
                    </DynamicLink>
                )
            })}
        </div>
    )
}

export default ColorSwatch
